/* =============================================================================
ContactPage_v03a.tsx

Version: v03a
Date: 2026-02-21

Description:
- Extracted from LiveAgent365Page_v02_v3g_A2P.tsx (previously ContactRoute_v03a)
- Drop-in contact page route component
- Adds LiveAgent365Header at the top of the page
- No behavior changes intended
============================================================================= */

import React from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Form,
  InputGroup,
  Alert,
} from "react-bootstrap";
import { Telephone, Envelope, CheckCircleFill } from "react-bootstrap-icons";
import { LiveAgent365Header } from "./LiveAgent365Header_v03";
import { MASTER_CONFIG_OPTIONS } from "../../../00MasterConfig/MasterSystemConfig";


// ===== Local helpers (moved with ContactRoute_v03a) =====
const toggleInArray = (arr: string[], value: string) =>
  arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];

const digitsOnly = (s: string) => (s || "").replace(/\D/g, "");

// ===== Branding constants (kept consistent with main page) =====
const BRAND_NAME = "LiveAgent365";
const CONTACT_PHONE_DISPLAY = "(888) 230-1882";
const CONTACT_PHONE_TEL = "+18882301882";



/* =========================
   Shared types / constants
========================= */

const VERSION = "v02.0.0";
const API_URL = MASTER_CONFIG_OPTIONS.API_URL;

type WirePayload = Record<string, string>;

type VoiceAgentLeadPayload = {
  // Landing page fields
  fullName: string;
  email: string;
  phone: string;
  company: string;
  website: string;
  primaryObjectives: string[];
  callVolume: string;
  notes: string;

  smsConsent: boolean;
  smsConsentText: string;
  smsConsentSource: string;

  // Metadata
  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;
};

type ServerStatus = { ok: boolean; status: number; id?: string; message?: string } | null;

type LeadFormState = {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  website: string;

  primaryObjectives: string[];
  callVolume: string;
  notes: string;

  smsConsent: boolean;
  smsMarketingConsent: boolean;
  smsNonMarketingConsent: boolean;
};

const PRIMARY_OBJECTIVE_OPTIONS = [
  "Missed calls & lead capture",
  "After-hours answering",
  "Appointment requests",
  "Sales qualification",
  "Routing & support triage",
];

/* =========================
   API helpers
========================= */

function toWirePayloadStrings(p: VoiceAgentLeadPayload): WirePayload {
  const out: WirePayload = {};
  for (const [k, v] of Object.entries(p)) {
    if (typeof v === "string") out[k] = v;
    else if (typeof v === "number") out[k] = Number.isFinite(v) ? `${v}` : "";
    else if (typeof v === "boolean") out[k] = v ? "true" : "false";
    else if (v && typeof v === "object") {
      try {
        out[k] = JSON.stringify(v);
      } catch {
        out[k] = "";
      }
    } else out[k] = "";
  }
  return out;
}

async function postLead(
  payload: WirePayload
): Promise<{ ok: boolean; status: number; id?: string; message?: string }> {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    mode: "cors",
  });

  let resJson: unknown = null;
  try {
    const txt = await res.text();
    resJson = txt ? JSON.parse(txt) : null;
  } catch {
    resJson = null;
  }

  const asObj = (x: unknown): Record<string, unknown> | null =>
    x && typeof x === "object" && !Array.isArray(x)
      ? (x as Record<string, unknown>)
      : null;

  if (!res.ok) {
    const o = asObj(resJson);
    const msg =
      (o?.error as string) ||
      (o?.message as string) ||
      `Request failed: ${res.status}`;
    return { ok: false, status: res.status, message: msg };
  }

  const o = asObj(resJson);
  const id = (o?.id as string) || (o?.requestId as string) || undefined;
  return { ok: true, status: res.status, id };
}

/* =====================================================================================
  COMPONENT: RequestDemoPanel_v02_A2P
  - Copied as-is for Contact page encapsulation
===================================================================================== */

const RequestDemoPanel_v02_A2P: React.FC<{
  form: LeadFormState;
  submitting: boolean;
  submitError: string | null;
  submitOk: boolean;
  lastSubmitted: VoiceAgentLeadPayload | null;
  serverStatus: { ok: boolean; status: number; id?: string; message?: string } | null;
  onChange: (k: keyof LeadFormState, v: string | boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  onReset: () => void;
}> = (props) => {
  const showWarn = props.serverStatus && !props.serverStatus.ok;
  const smsConsentMissing = Boolean(props.submitError && /sms\s*consent/i.test(props.submitError));
  return props.submitOk && props.lastSubmitted ? (
    <Card className="shadow-sm border-0 rounded-4">
      <Card.Body className="p-4">
        <Alert variant="success" className="rounded-4">
          <div className="d-flex align-items-start gap-2">
            <CheckCircleFill className={`mt-1 ${smsConsentMissing ? "bg-warning bg-opacity-25 border border-warning rounded-3 p-2" : ""}`} />
            <div>
              <div className="fw-semibold">Thank you — we received your request!</div>
              <div className="text-muted">A representative will be following up shortly.</div>
            </div>
          </div>
        </Alert>

        <div className="small text-muted">
          <div className="mb-2">
            <span className="fw-semibold text-dark">Submitted:</span>{" "}
            {props.lastSubmitted.fullName} • {props.lastSubmitted.email} • {props.lastSubmitted.phone}
          </div>
          {props.lastSubmitted.company ? (
            <div className="mb-2">
              <span className="fw-semibold text-dark">Company:</span> {props.lastSubmitted.company}
            </div>
          ) : null}
          {props.lastSubmitted.website ? (
            <div className="mb-2">
              <span className="fw-semibold text-dark">Website:</span> {props.lastSubmitted.website}
            </div>
          ) : null}
        </div>

        <div className="d-flex flex-column flex-sm-row gap-2 mt-3">
          <Button className="rounded-pill px-4" variant="primary" onClick={props.onReset}>
            Submit another request
          </Button>
          <Button className="rounded-pill px-4" variant="outline-secondary" href="/">
            Back to top
          </Button>
        </div>
      </Card.Body>
    </Card>
  ) : (
    <Card className="shadow-sm border-0 rounded-4">
      <Card.Body className="p-4">
        {props.submitError ? (
          <Alert variant="danger" className="rounded-4">
            <div className="fw-semibold mb-1">Please fix the following:</div>
            <div style={{ whiteSpace: "pre-line" }}>{props.submitError}</div>
          </Alert>
        ) : null}

        {showWarn ? (
          <Alert variant="warning" className="rounded-4">
            {props.serverStatus?.message || "There was a problem submitting your request."}
          </Alert>
        ) : null}

        <Form onSubmit={props.onSubmit}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>Full name *</Form.Label>
              <Form.Control
                value={props.form.fullName}
                onChange={(e) => props.onChange("fullName", e.target.value)}
                placeholder="Jordan Miller"
                className="rounded-3"
                disabled={props.submitting}
              />
            </Col>

            <Col md={6}>
              <Form.Label>Email *</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <Envelope />
                </InputGroup.Text>
                <Form.Control
                  value={props.form.email}
                  onChange={(e) => props.onChange("email", e.target.value)}
                  placeholder="you@company.com"
                  className="rounded-end-3"
                  disabled={props.submitting}
                />
              </InputGroup>
            </Col>

            <Col md={6}>
              <Form.Label>Phone *</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <Telephone />
                </InputGroup.Text>
                <Form.Control
                  value={props.form.phone}
                  onChange={(e) => props.onChange("phone", e.target.value)}
                  placeholder="(555) 555-5555"
                  className="rounded-end-3"
                  disabled={props.submitting}
                />
              </InputGroup>
              
            </Col>

            <Col md={6}>
              <Form.Label>Company</Form.Label>
              <Form.Control
                value={props.form.company}
                onChange={(e) => props.onChange("company", e.target.value)}
                placeholder="Your company"
                className="rounded-3"
                disabled={props.submitting}
              />
            </Col>


            <Col md={12}>
                <div className="small text-muted mt-1">We’ll use this information for follow-up and onboarding purposes only.
                  <br></br>
                  <b><i>We will not sell your data, bother you or spam you. </i></b>
                  </div>
            </Col>


            <Col md={12}>
              
              <Form.Label>Website</Form.Label>
              <Form.Control
                value={props.form.website}
                onChange={(e) => props.onChange("website", e.target.value)}
                placeholder="https://example.com"
                className="rounded-3"
                disabled={props.submitting}
              />
            </Col>

            {/* <Col md={12}>
              <Form.Label>Primary objectives *</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {PRIMARY_OBJECTIVE_OPTIONS.map((opt) => (
                  <Button
                    key={opt}
                    size="sm"
                    type="button"
                    variant={props.form.primaryObjectives.includes(opt) ? "primary" : "outline-primary"}
                    className="rounded-pill px-3"
                    disabled={props.submitting}
                    onClick={() =>
                      props.onChange(
                        "primaryObjectives",
                        toggleInArray(props.form.primaryObjectives, opt).join("|||")
                      )
                    }
                  >
                    {opt}
                  </Button>
                ))}
              </div>
              <div className="small text-muted mt-2">
                Tip: pick the top 1–3 outcomes you want LiveAgent365 to handle first.
              </div>
            </Col> */}


      {/* <Col md={12}>
              <Form.Label>Primary objectives *</Form.Label>

              <div className="d-flex flex-column gap-2">
                {PRIMARY_OBJECTIVE_OPTIONS.map((opt) => (
                  <Form.Check
                    key={opt}
                    type="checkbox"
                    id={`primaryObjective-${opt}`}
                    label={opt}
                    disabled={props.submitting}
                    checked={props.form.primaryObjectives.includes(opt)}
                    onChange={() =>
                      props.onChange(
                        "primaryObjectives",
                        toggleInArray(props.form.primaryObjectives, opt).join("|||")
                      )
                    }
                  />
                ))}
              </div>

              <div className="small text-muted mt-2">
                Tip: pick the top 1–3 outcomes you want LiveAgent365 to handle first.
              </div>
            </Col> */}
                

        <Col md={12}>
            <Form.Label>Primary objectives *</Form.Label>

            <div className="p-3 rounded-3 border bg-white">
              <Row className="g-2">
                {PRIMARY_OBJECTIVE_OPTIONS.map((opt) => {
                  const checked = props.form.primaryObjectives.includes(opt);

                  return (
                    <Col key={opt} xs={12} md={6}>
                      <div className="d-flex align-items-start">
                        <Form.Check
                          type="checkbox"
                          id={`primaryObjective-${opt}`}
                          className="mb-0"
                          label={<span className="small">{opt}</span>}
                          disabled={props.submitting}
                          checked={checked}
                          onChange={() =>
                            props.onChange(
                              "primaryObjectives",
                              toggleInArray(props.form.primaryObjectives, opt).join("|||")
                            )
                          }
                        />
                      </div>
                    </Col>
                  );
                })}
              </Row>

              <div className="small text-muted mt-2">
                {/* Tip: pick the top 1–3 outcomes you want LiveAgent365 to handle first. */}
                Choose all that apply.
              </div>
            </div>
          </Col>


            <Col md={12}>
              <Form.Label>Estimated call volume</Form.Label>
              <Form.Select
                className="rounded-3"
                value={props.form.callVolume}
                disabled={props.submitting}
                onChange={(e) => props.onChange("callVolume", e.target.value)}
              >
                <option>0–20 calls/day</option>
                <option>21–50 calls/day</option>
                <option>51–100 calls/day</option>
                <option>101–250 calls/day</option>
                <option>250+ calls/day</option>
              </Form.Select>
            </Col>

            <Col md={12}>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={props.form.notes}
                onChange={(e) => props.onChange("notes", e.target.value)}
                placeholder="Anything we should know? (hours, services, booking preferences, systems, etc.)"
                className="rounded-3"
                disabled={props.submitting}
              />
            </Col>

            
            <Col md={12}>
              <style>{`                #smsConsent.form-check-input,
                #smsMarketingConsent.form-check-input,
                #smsNonMarketingConsent.form-check-input {
                  border: 3px solid var(--bs-primary);
                }
                .sms-consent-highlight .form-check-input {
                  border: 3px solid var(--bs-primary);
                  box-shadow: 0 0 0 .3rem rgba(247,201,72,0.35);
                }.consent-help {
                  margin-left: 1.75rem; /* align with checkbox label text */
                }
              `}</style>
              <div className={(smsConsentMissing ? "sms-consent-highlight " : "") + "mt-2"}>
                <Form.Check
                  type="checkbox"
                  id="smsMarketingConsent"
                  className="mt-1"
                  checked={Boolean(props.form.smsMarketingConsent)}
                  disabled={props.submitting}
                  onChange={(e) => props.onChange("smsMarketingConsent", e.target.checked)}
                  label={
                    <span className="small">
                      I consent to receive <span className="fw-semibold">marketing</span> text messages from{" "}
                      <span className="fw-semibold">LiveAgent365</span> at the phone number provided. Frequency may vary.
                      Message &amp; data rates may apply. Text HELP for assistance, reply STOP to opt out.
                    </span>
                  }
                />
                <Form.Check
                  type="checkbox"
                  id="smsNonMarketingConsent"
                  className="mt-1"
                  checked={Boolean(props.form.smsNonMarketingConsent)}
                  disabled={props.submitting}
                  onChange={(e) => props.onChange("smsNonMarketingConsent", e.target.checked)}
                  label={
                    <span className="small">
                      I consent to receive <span className="fw-semibold">non-marketing</span> text messages from{" "}
                      <span className="fw-semibold">LiveAgent365</span> about my order updates, appointment reminders etc.
                      Frequency may vary. Message &amp; data rates may apply. Text HELP for assistance, reply STOP to opt out.
                    </span>
                  }
                />
              </div>
            </Col>

            <Col md={12} className="mt-2">
              <Button
                type="submit"
                variant="primary"
                className="w-100 rounded-pill py-2"
                disabled={props.submitting}
              >
                {props.submitting ? "Submitting..." : "Request a demo"}
              </Button>
              <div className="small text-muted mt-2 text-center">
                We respect your inbox. You can opt out anytime. For SMS, reply STOP to unsubscribe.
              </div>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );
};

/* =====================================================================================
  PAGE: ContactPage_v03a (was ContactRoute_v03a)
===================================================================================== */

const ContactPage_v03a: React.FC = () => {
  const SMS_CONSENT_TEXT =
    "By checking the box, you agree to receive SMS text messages from LiveAgent365. Msg & data rates may apply. Msg frequency varies. Reply STOP to opt out; HELP for help. Consent is not a condition of purchase.";
type LeadFormState = {
    fullName: string;
    email: string;
    phone: string;
    company: string;
    website: string;
    primaryObjectives: string[];
    callVolume: string;
    notes: string;
    smsConsent: boolean;
    smsMarketingConsent: boolean;
    smsNonMarketingConsent: boolean;
};

  const DEFAULT_FORM: LeadFormState = {
    fullName: "",
    email: "",
    phone: "",
    company: "",
    website: "",
    primaryObjectives: ["Missed calls & lead capture"],
    callVolume: "0–20 calls/day",
    notes: "",
    smsConsent: false,
  smsMarketingConsent: false,
    smsNonMarketingConsent: false,
};

  const [form, setForm] = React.useState<LeadFormState>(DEFAULT_FORM);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitOk, setSubmitOk] = React.useState<boolean>(false);
  const [lastSubmitted, setLastSubmitted] = React.useState<VoiceAgentLeadPayload | null>(null);
  const [serverStatus, setServerStatus] = React.useState<ServerStatus>(null);

  const handlePanelChange = (key: string, value: string | boolean) => {
    setSubmitOk(false);
    setSubmitError(null);
    setServerStatus(null);

    setForm((p) => {
      if (key === "primaryObjectives") {
        const next = String(value || "")
          .split("|||")
          .map((s) => s.trim())
          .filter(Boolean);
        return { ...p, primaryObjectives: next };
      }

      if (key === "smsConsent") {
        const next = value === true || value === "true";
        return { ...p, smsConsent: next };
      }

      return { ...p, [key]: String(value ?? "") } as LeadFormState;
    });
  };

  const handlePanelReset = () => {
    setForm(DEFAULT_FORM);
    setSubmitting(false);
    setSubmitError(null);
    setSubmitOk(false);
    setLastSubmitted(null);
    setServerStatus(null);
  };

  function buildLeadPayload(): VoiceAgentLeadPayload {
    return {
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      phone: digitsOnly(form.phone),
      company: form.company.trim(),
      website: (form.website || "").trim(),

      primaryObjectives: form.primaryObjectives,
      callVolume: form.callVolume,
      notes: (form.notes || "").trim(),

      smsConsent: Boolean(form.smsConsent),
      smsConsentText: SMS_CONSENT_TEXT,
      smsConsentSource: "web_form",
domainName: typeof window !== "undefined" ? String(window.location.hostname) : "unknown",
      itemCategory: "Customer.liveagent365.demo",
      itemClass: "demo.request",

      // If you want it IDENTICAL to landing page, set this to "liveagent365.landing"
      itemType: "liveagent365.contact",

      clientVersion: String(VERSION),
    };
  }

  function validate(): string | null {
    const emailOk = /^\S+@\S+\.\S+$/.test(form.email.trim());
    const nameOk = form.fullName.trim().length >= 2;

    const phoneDigits = digitsOnly(form.phone);
    const phoneRequiredOk = phoneDigits.length === 10; // match landing page requirement
    const objectivesOk = (form.primaryObjectives || []).length >= 1;
    const smsOk = true; // SMS checkboxes are optional (A2P)
if (!nameOk) return "Please enter your name.";
    if (!emailOk) return "Please enter a valid email address.";
    if (!phoneRequiredOk) return "Please enter a 10-digit phone number.";
    if (!objectivesOk) return "Please select at least one objective.";
if (!smsOk) return `SMS consent required:
• Please check the SMS opt-in box (below) before requesting a demo.`;
    return null;
  }

  const handlePanelSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationMsg = validate();
    if (validationMsg) {
      setSubmitOk(false);
      setSubmitError(validationMsg);
      setServerStatus({ ok: false, status: 400, message: validationMsg });
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitOk(false);
    setServerStatus(null);

    try {
      const payload = buildLeadPayload();
      const wirePayload = toWirePayloadStrings(payload);

      // ✅ Same API pipeline as landing page
      const status = await postLead(wirePayload);
      setServerStatus(status);

      if (!status.ok) throw new Error(status.message || "Unknown server error");

      setLastSubmitted(payload);
      setSubmitOk(true);
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : "Something went wrong.";
      const msg = `We couldn’t submit your request.\n• Please try again in a moment.\n• If the problem continues, refresh the page and try once more.\n\nDetails: ${raw}`;
      setSubmitOk(false);
      setSubmitError(msg);
      setServerStatus({ ok: false, status: 500, message: raw });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <LiveAgent365Header brandName={BRAND_NAME} phoneDisplay={CONTACT_PHONE_DISPLAY} phoneTel={CONTACT_PHONE_TEL} />
      <Container className="py-5">
      <Row className="g-4">
        <Col lg={5}>
          <Card className="shadow-sm border-0 rounded-4">
            <Card.Body className="p-4">
              <h2 className="fw-bold mb-3">Contact</h2>

              <div className="text-muted mb-2">
                <div className="fw-semibold text-dark">Business</div>
                <div>{BRAND_NAME}</div>
              </div>

              <div className="text-muted mb-2">
                <div className="fw-semibold text-dark">Email</div>
                <div>
                  <a href="mailto:support@liveagent365.com" className="text-decoration-none">
                    support@liveagent365.com
                  </a>
                </div>
              </div>

              <div className="text-muted mb-2">
                <div className="fw-semibold text-dark">Phone</div>
                <div>
                  <a href={`tel:${CONTACT_PHONE_TEL}`} className="text-decoration-none">
                    {CONTACT_PHONE_DISPLAY}
                  </a>
                </div>
              </div>

              <div className="text-muted">
                <div className="fw-semibold text-dark">Address</div>
                <div>1312 17th St, Suite: 364 Denver, CO 80202</div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <RequestDemoPanel_v02_A2P
            form={form}
            submitting={submitting}
            submitError={submitError}
            submitOk={submitOk}
            lastSubmitted={lastSubmitted}
            serverStatus={serverStatus}
            onChange={handlePanelChange}
            onSubmit={handlePanelSubmit}
            onReset={handlePanelReset}
          />

          
        </Col>
      </Row>
    </Container>
    </>
  );
};

export default ContactPage_v03a;

/* =============================================================================
File: ContactPage_v03a.tsx
Version: v03a
============================================================================= */
